import { FORGE_UI_EXTENSIONS_EXPERIENCE } from '@confluence/experience-tracker/entry-points/ExperienceName.ts';
import { getExperienceTracker } from '@confluence/experience-tracker/entry-points/getExperienceTracker.ts';

/**
 * Narrow shape of the Forge UI extension event data.
 * Only type the fields actually read; all optional because data may come from untrusted sources.
 */
export interface ForgeUIExtensionEventData {
	/** The type of Forge UI extension (e.g. 'keyboard-shortcut', 'bridge'). */
	extensionType?: string;
	/** The Forge app ID. */
	appId?: string;
}

type MaybeForgeUIExtensionEventData = ForgeUIExtensionEventData | null | undefined;

type ForgeUIExtensionsExperienceTrackerSupportApi = {
	isTrackable(data: MaybeForgeUIExtensionEventData): boolean;
	start(data: MaybeForgeUIExtensionEventData): void;
	succeed(): void;
	fail(messageOrError: string | Error): void;
	abort(reason: string): void;
};

const getError = (messageOrError: string | Error): Error => {
	if (messageOrError instanceof Error) {
		return messageOrError;
	}

	return new Error(messageOrError);
};

/**
 * Singleton that encapsulates ExperienceTracker calls for the Forge UI extensions experience.
 *
 * The experience name is intentionally shared across all Forge UI extension types
 * (keyboard shortcuts, bridge, etc.). Attach the `extensionType` dimension attribute
 * so TOME SLOs can be scoped per capability without needing separate experience names.
 */
export const ForgeUIExtensionsExperienceTrackerSupport: ForgeUIExtensionsExperienceTrackerSupportApi =
	new (class ForgeUIExtensionsExperienceTrackerSupport {
		/**
		 * Returns true when the event data represents a trackable Forge UI extension instance.
		 * Guard this before calling start/succeed/fail/abort.
		 */
		isTrackable(data: MaybeForgeUIExtensionEventData): boolean {
			return Boolean(data?.extensionType);
		}

		start(data: MaybeForgeUIExtensionEventData): void {
			getExperienceTracker().start({
				name: FORGE_UI_EXTENSIONS_EXPERIENCE,
				attributes: {
					extensionType: data?.extensionType,
					appId: data?.appId,
				},
			});
		}

		succeed(): void {
			getExperienceTracker().succeed({
				name: FORGE_UI_EXTENSIONS_EXPERIENCE,
			});
		}

		fail(messageOrError: string | Error): void {
			getExperienceTracker().fail({
				name: FORGE_UI_EXTENSIONS_EXPERIENCE,
				error: getError(messageOrError),
			});
		}

		abort(reason: string): void {
			getExperienceTracker().abort({
				name: FORGE_UI_EXTENSIONS_EXPERIENCE,
				reason,
			});
		}
	})();
